import { useCallback, useEffect, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { shopModeApi } from "../../api/shopMode";
import type { ShopifyOrderView, ShopifyPassedAway } from "../../api/types";
import { useBoxPickPrompt } from "./useBoxPickPrompt";
import { BoxPromptCancelled } from "../../utils/boxConfirmation";

import { useT } from "../../i18n/LanguageProvider";
import { dateText } from "../../i18n/intl";
import type { TranslateFn } from "../../i18n/t";
import { categoryLabel, colourLabel } from "../../i18n/taxonomy";

function lineSummary(order: ShopifyOrderView): string {
  return order.lines.map((line) => `${line.quantity} x ${line.titleRaw}`).join(", ");
}

/** A Barcelona calendar day the server sent (YYYY-MM-DD) in the florist's language. */
function dayText(ymd: string, options: Intl.DateTimeFormatOptions): string {
  const [year, month, day] = ymd.split("-").map(Number);
  return dateText(new Date(Date.UTC(year, month - 1, day)), { ...options, timeZone: "UTC" });
}

/**
 * G-167 (OD-283): the delivery day as a pill — "Today · 16:00–20:00",
 * "Tomorrow · …", or the date for a later (or missed) day. Nothing for an
 * undated order: the day is not known.
 */
function DuePill({ order, t }: { order: ShopifyOrderView; t: TranslateFn }) {
  const due = order.due;
  if (!due) return null;
  const day =
    due.when === "TODAY"
      ? t("shopify.dueToday")
      : due.when === "TOMORROW"
        ? t("shopify.dueTomorrow")
        : dayText(due.day, { weekday: "short", day: "numeric", month: "short" });
  const tone = due.when === "TODAY" ? "is-today" : due.when === "TOMORROW" ? "is-tomorrow" : due.when === "PAST" ? "is-past" : "is-later";
  return (
    <span className={`shop-floor-shopify-due ${tone}`}>
      {due.window && due.when !== "LATER" ? t("shopify.dueAt", { day, window: due.window }) : day}
    </span>
  );
}

/** G-167: before its preparation day an office-placed order waits on the card, with nothing to do yet. */
function waitsForPrepDay(order: ShopifyOrderView): boolean {
  return order.due?.when === "LATER";
}

/** G-167: how the order came to this shop when the office placed it, and when to prepare it. */
function OfficeNote({ order, t }: { order: ShopifyOrderView; t: TranslateFn }) {
  const prepDay = waitsForPrepDay(order) && order.due
    ? dayText(order.due.prepDay, { weekday: "short", day: "numeric" })
    : null;
  if (order.lastPass) {
    return (
      <>
        <p className="muted">{t("shopify.passedFrom", { shop: order.lastPass.from.name, reason: order.lastPass.reason })}</p>
        {prepDay ? <p className="muted">{t("shopify.prepareOn", { day: prepDay })}</p> : null}
      </>
    );
  }
  if (order.assigned) {
    return (
      <p className="muted">
        {prepDay ? t("shopify.assignedPrepareOn", { day: prepDay }) : t("shopify.assignedByOffice")}
      </p>
    );
  }
  return null;
}

/** OD-102: what this shop still has to bring in from elsewhere, one line each. */
function TransferPlanLines({ order, t }: { order: ShopifyOrderView; t: TranslateFn }) {
  if (!order.transferPlan?.length) return null;
  return (
    <div className="shop-floor-shopify-order-plan">
      <p className="muted">{t("shopify.offeredHere")}</p>
      {order.transferPlan.map((transfer, index) => (
        <p key={`${index}-${transfer.itemId ?? transfer.description}`}>
          {transfer.packaging
            ? t("shopify.needsRollFrom", {
                what: transfer.packaging.colorNames.length
                  ? `${categoryLabel(transfer.packaging.categoryCode)} (${transfer.packaging.colorNames
                      .map((name) => colourLabel(name))
                      .join(", ")})`
                  : categoryLabel(transfer.packaging.categoryCode),
                shop: transfer.fromLocationName,
              })
            : t("shopify.needsFrom", {
                qty: String(Number(transfer.qty)),
                what: transfer.description,
                shop: transfer.fromLocationName,
              })}
        </p>
      ))}
    </div>
  );
}

export function ShopFloorShopifyOrdersPanel({
  onBuildLine,
}: {
  onBuildLine: (target: {
    orderId: string;
    lineId: string;
    templateId?: string | null;
    title?: string;
  }) => void;
}) {
  const { t } = useT();
  const [available, setAvailable] = useState<ShopifyOrderView[]>([]);
  const [mine, setMine] = useState<ShopifyOrderView[]>([]);
  const [passedAway, setPassedAway] = useState<ShopifyPassedAway[]>([]);
  const [error, setError] = useState<string | null>(null);
  // KI-75: a refused Accept or Hand to delivery stays on screen until the
  // next tap — the ten-second refresh clears only its own load error.
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [availableRows, mineRows, passedRows] = await Promise.all([
        shopModeApi.floorListAvailableShopifyOrders(),
        shopModeApi.floorListMyShopifyOrders(),
        shopModeApi.floorListPassedAwayShopifyOrders(),
      ]);
      setAvailable(availableRows);
      setMine(mineRows);
      setPassedAway(passedRows);
    } catch (e) {
      setError(
        errorText(e, "shopify.couldNotLoad"),
      );
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 10000);
    return () => window.clearInterval(timer);
  }, [load]);

  const accept = async (id: string) => {
    setBusyId(id);
    setActionError(null);
    try {
      await shopModeApi.floorAcceptShopifyOrder(id);
    } catch (e) {
      setActionError(errorText(e, "shopify.couldNotAccept"));
    } finally {
      setBusyId(null);
    }
    await load();
  };

  // G-112 review fix (OD-47): selling a Shopify order's shop item that
  // lives in a storage box asks "Is this from box X?", like the till.
  const boxPrompt = useBoxPickPrompt();

  const fulfil = async (id: string) => {
    setBusyId(id);
    setActionError(null);
    try {
      await boxPrompt.run((boxAnswers) =>
        shopModeApi.floorFulfilShopifyOrder(id, boxAnswers),
      );
    } catch (e) {
      // Backing out of the box question records nothing and is no refusal.
      if (!(e instanceof BoxPromptCancelled)) setActionError(errorText(e, "shopify.couldNotHandOver"));
    } finally {
      setBusyId(null);
    }
    await load();
  };

  return (
    <section className="card shop-floor-shopify-orders-panel">
      <div className="shop-floor-shopify-orders-header">
        <h3>{t("shopify.title")}</h3>
        <p className="muted">{t("shopify.updatesAutomatically")}</p>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {actionError ? <div className="alert error">{actionError}</div> : null}

      <div className="shop-floor-shopify-orders-columns">
        <section className="shop-floor-shopify-orders-column">
          <div className="shop-floor-shopify-orders-column-header">
            <h4>{t("shopify.available")}</h4>
            <span className="shop-floor-shopify-orders-count">{available.length}</span>
          </div>
          {available.length === 0 ? (
            <p className="muted shop-floor-shopify-orders-empty">
              {t("shopify.noAvailable")}
            </p>
          ) : null}
          <div className="shop-floor-shopify-orders-list">
            {available.map((order) => (
              <div key={order.id} className="card shop-floor-shopify-order-card">
                <div className="shop-floor-shopify-order-card-row">
                  <div>
                    <div className="shop-floor-shopify-order-title">
                      <strong>{order.orderNumber ?? order.shopifyOrderId}</strong>
                      <DuePill order={order} t={t} />
                    </div>
                    <p className="muted">{lineSummary(order)}</p>
                    <TransferPlanLines order={order} t={t} />
                  </div>
                  <button
                    type="button"
                    className="button"
                    disabled={busyId === order.id}
                    onClick={() => void accept(order.id)}
                  >
                    {busyId === order.id ? t("shopify.accepting") : t("shopify.accept")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="shop-floor-shopify-orders-column">
          <div className="shop-floor-shopify-orders-column-header">
            <h4>{t("shopify.preparing")}</h4>
            <span className="shop-floor-shopify-orders-count">{mine.length}</span>
          </div>
          {mine.length === 0 ? (
            <p className="muted shop-floor-shopify-orders-empty">
              {t("shopify.noClaimed")}
            </p>
          ) : null}
          {passedAway.map((pass) => (
            <p key={pass.orderId} className="muted shop-floor-shopify-orders-empty">
              {t("shopify.passedAway", { order: pass.orderNumber, shop: pass.toLocationName, reason: pass.reason })}
            </p>
          ))}
          <div className="shop-floor-shopify-orders-list">
            {mine.map((order) => {
              const bouquetLines = order.lines.filter(
                (line) => line.kind === "BOUQUET",
              );
              const allBuilt = bouquetLines.every((line) => line.bouquetBuildId);
              const waiting = waitsForPrepDay(order);
              return (
                <div
                  key={order.id}
                  className={`card shop-floor-shopify-order-card${waiting ? " is-waiting" : ""}`}
                >
                  <div>
                    <div className="shop-floor-shopify-order-title">
                      <strong>{order.orderNumber ?? order.shopifyOrderId}</strong>
                      <DuePill order={order} t={t} />
                    </div>
                    <p className="muted">{lineSummary(order)}</p>
                    <OfficeNote order={order} t={t} />
                  </div>
                  {order.shopifyChange ? (
                    <p className="alert error">{t("shopify.changedAfterAccept")}</p>
                  ) : null}
                  <TransferPlanLines order={order} t={t} />
                  {(order.prepShortfall ?? []).map((line, index) => (
                    <p key={`${index}-${line.description}`} className="alert error">
                      {t("shopify.notInAnyShop", { what: line.description })}
                    </p>
                  ))}
                  {waiting ? null : bouquetLines.map((line) => (
                    <div
                      key={line.id}
                      className="shop-floor-shopify-order-line"
                    >
                      <span>
                        {line.titleRaw}
                        {line.bouquetBuildId ? ` ${t("shopify.built")}` : ""}
                      </span>
                      {!line.bouquetBuildId ? (
                        <button
                          type="button"
                          className="button tinted"
                          onClick={() =>
                            onBuildLine({
                              orderId: order.id,
                              lineId: line.id,
                              templateId: line.bouquetTemplateId,
                              title: line.titleRaw,
                            })
                          }
                        >
                          {t("shopify.build")}
                        </button>
                      ) : null}
                    </div>
                  ))}
                  {waiting ? null : (
                    <>
                      <p className="muted shop-floor-shopify-order-hint">
                        {t("shopify.checkPackaging")}
                      </p>
                      <button
                        type="button"
                        className="button"
                        disabled={!allBuilt || busyId === order.id}
                        onClick={() => void fulfil(order.id)}
                      >
                        {busyId === order.id ? t("shopify.handingOff") : t("shopify.handToDelivery")}
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
      {boxPrompt.element}
    </section>
  );
}
